import type { Unit } from 'effector'
import type { ComponentClass, FunctionComponent } from 'react'
import type {
  Route,
  RouteContext,
  RouteFilterFn,
  RouteFilterSourced,
  RouteParams,
  RouterContext,
} from 'universal-router'
import type { Key } from '~/shared/jail'

import UniversalRouter from 'universal-router'
import { isFunction, isPlainObject, isPromise, toArray } from '~/shared/helpers'
import { logger } from '~/shared/logger'
import { ResolveLoadError, getErrorMessage } from './errors'

// re-export from 'universal-router' to lessen dependency, just in case
export type { Route }

export type RouterResponse = {
  keys?: Key | Key[]
  filter?: RouteFilterFn | RouteFilterSourced<any>
  component?:
    | FunctionComponent<any>
    | ComponentClass<any>
    | Array<FunctionComponent<any> | ComponentClass<any>>
  props?: any
  init?: Unit<any>
  left?: Unit<any>
  redirect?: string
  meta?: Record<string, any> // any additional metadata for the route
  __resolved?: unknown // actual resolved route, stable by reference
}

type RouterResponseEx = RouterResponse | undefined | null

// top-level routes object
const routes: Route = {}

// router instance
export const router: UniversalRouter<RouterResponse> =
  new UniversalRouter<RouterResponse>(routes, {
    resolveRoute,
    errorHandler(error, context) {
      logger.error('🚨 Router error', { error, context })

      const query = context.query instanceof URLSearchParams ? context.query.toString() : '' // prettier-ignore
      const search = query ? `?${query}` : ''
      const from = context.pathname + search

      return {
        redirect: `/error?${new URLSearchParams({
          from,
          message: getErrorMessage(error),
        })}`,
        props: {
          state: {
            errorMessage: getErrorMessage(error),
            params: {
              pathname: context.pathname,
              search,
              hash: '',
            },
          },
        },
      }
    },
  })

/**
 * Re-Instantiate router with new routes
 */
export function init(appRoutes: Route) {
  Object.assign(routes, impregnate(appRoutes))
}

/**
 * Overridden resolve route for universal router,
 * handles dynamic routes and other custom properties
 */
function resolveRoute(
  context: RouteContext<RouterResponse, RouterContext>,
  params: RouteParams
): RouterResponseEx | Promise<RouterResponseEx> {
  const { route, options, features, session, read } = context

  if (route.load) {
    if (isPlainObject(route.load)) {
      return resolveSyncRoute(context, params)
    }

    return resolveAsyncRoute(context, params).catch((error) => {
      logger.error('🔁', error)

      // bust import cache
      // dynamic imports are caching results, regardless of success or error
      // there is a crunchy ad hoc solution for this
      // see https://medium.com/@alonmiz1234/retry-dynamic-imports-with-react-lazy-c7755a7d557a
      if (/Failed to fetch dynamically imported module/.test(error.message)) {
        // extract url from error
        const url = new URL(
          error.message
            .replace('Failed to fetch dynamically imported module: ', '')
            .trim()
        )
        // add a timestamp to the url to force a reload the module (and not use the cached version - cache busting)
        url.searchParams.set('t', `${Date.now()}`)
        // replace load function for route with the new url
        route.load = () => import(/* @vite-ignore */ url.href)
      }

      // TODO: think about how to handle different errors here
      // I couldn't find any way to distinguish between network errors and server errors here
      // dynamic import will fail with the same error on 404 and when offline :(
      throw error instanceof ResolveLoadError
        ? error
        : new ResolveLoadError(error)
    })
  }

  // `action` is "built-in" way to handle route in universal-router, and I keep it same way
  if (isFunction(route.action)) {
    const result: RouterResponseEx | Promise<RouterResponseEx> = //
      route.action(context, params)

    return isPromise(result)
      ? Promise.resolve(result).then((result) => merge(context, result))
      : merge(context, result)
  }

  // `fn` is essentially same as `action`, but with different signature, and allows "sourced" function
  if (route.fn != null) {
    const ctx = { options, features, session }
    const result: RouterResponseEx | Promise<RouterResponseEx> = //
      isFunction(route.fn)
        ? route.fn(params, ctx)
        : route.fn.fn(read(route.fn.source), params, ctx)

    return isPromise(result)
      ? Promise.resolve(result).then((result) => merge(context, result))
      : merge(context, result)
  }

  // `filter` allows to skip route (and all its children) based on some condition
  if (route.filter != null) {
    const ctx = { options, features, session }
    const result = isFunction(route.filter)
      ? route.filter(params, ctx)
      : route.filter.fn(read(route.filter.source), params, ctx)

    if (result === false) {
      return null // `null` means skip this and all nested routes, and go to the next sibling route
    }
  }

  // if there are no children, then this route must be final
  if (route.children == null || route.children.length === 0) {
    return merge(context, {})
  }

  return undefined // undefined means router will try to match the child routes
}

/**
 * Synchronously resolves route for universal router
 */
function resolveSyncRoute(
  context: RouteContext<RouterResponse, RouterContext>,
  params: RouteParams
): RouterResponseEx | Promise<RouterResponseEx> {
  const { route } = context

  const extend = route.load as Route
  delete route.load // load only once
  delete route.children // orphan all children, so universal router could do some optimisations
  Object.assign(route, impregnate(extend)) // but route could contain more loaders

  return resolveRoute(context, params)
}

/**
 * Asynchronously resolves route for universal router,
 * recursively calls `resolveRoute` after async load
 */
async function resolveAsyncRoute(
  context: RouteContext<RouterResponse, RouterContext>,
  params: RouteParams
): Promise<RouterResponseEx> {
  const { route } = context

  const load = isFunction(route.load) ? route.load() : route.load
  const mod = await Promise.resolve(load)

  // extends current context route
  if (mod != null) {
    const extend = 'default' in mod ? mod.default : mod
    if (extend != null) {
      delete route.load // load only once
      delete route.children // orphan all children, so universal router could do some optimisations
      Object.assign(route, impregnate(extend)) // but route could contain more loaders
    }
  }

  return resolveRoute(context, params)
}

/**
 * Adds empty children array to route in case it contains load function
 * This is needed for universal router to work properly,
 * because otherwise it marks matcher as final, if there are no children
 * https://github.com/kriasoft/universal-router/blob/f55d1cefe98bcbbaa427d3777b53ebc75541dcfb/src/universal-router.ts#L186
 */
function impregnate(route: Route): Route {
  // has loader and has no children -> add empty children array
  if (route.load && !route.children) {
    route.children = []
  }

  // has children -> recursively impregnate each child
  else if (route.children && route.children.length) {
    for (const child of route.children) {
      impregnate(child)
    }
  }

  return route
}

/**
 * Slot fake component, just to mark place in the component tree
 * that will be filled with child route components
 */
export const Slot = Symbol() as unknown as FunctionComponent<unknown>

/**
 * Merges route with `action` function result
 */
function merge(
  context: RouterContext,
  result?: RouterResponseEx
): RouterResponseEx {
  if (result != null) {
    // collect nearest keys
    let keys: Key | Key[] | undefined

    // collect all components
    let component: NonNullable<RouterResponse['component']> = []

    // from the route tree up to the root
    let route: Route | null | undefined = context.route
    while (route) {
      if (keys == null && route.keys != null) {
        keys = route.keys
      }
      if (route.component != null) {
        const arr = toArray(route.component)
        const slotIdx = arr.indexOf(Slot)
        component =
          slotIdx > -1
            ? arr.toSpliced(slotIdx, 1, ...component) // found Slot, insert previously collected components here
            : arr.concat(component) // no Slot, just append previously collected components
      }
      route = route.parent
    }

    // return merged route result
    return {
      keys,
      component,
      init: context.route.init,
      left: context.route.left,
      ...result,
      props: {
        query: context.query as URLSearchParams,
        ...context.params,
        ...result.props,
      },
      __resolved: context.route,
    }
  }

  return result // null or undefined
}
